import { useState } from 'react';
import createNetworkingClient from 'utils/networking-client';
import { devLog, isDev } from '../utils';

const useFetchFeedEntries = ({
  clubId,
  feedType,
  currentAthleteId,
  page,
  useMockData,
  preFetchedEntries
}) => {
  const [entries, setEntries] = useState(preFetchedEntries || []); // profile-feed currently pre-fetches entry data and does not use
  const [pagination, setPagination] = useState({
    maxEntries: true,
    hasMore: page !== 'profile' // profile feed does not use pagination
  });
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const errorState = (e) => {
    setError(e);
    setIsLoading(false);
    // TODO [monorepo] - log error to sentry
  };

  const buildEndpointUrl = (cursorData) => {
    const { before, rank } = cursorData; // See README for how before and rank cursor works

    let url;
    switch (page) {
      case 'dashboard':
        url = `/dashboard/feed`;
        break;
      case 'club':
        url = `/clubs/${clubId}/feed`;
        break;
      case 'profile':
        // profile feed entries are pre-fetched since it is still reloaded on interval graph filters and is not paginated
        return null;
      default:
        return null;
    }

    url = url.concat(`?feed_type=${feedType}`);
    url = currentAthleteId
      ? url.concat(`&athlete_id=${currentAthleteId}`)
      : url;
    url = clubId ? url.concat(`&club_id=${clubId}`) : url;
    url = before ? url.concat(`&before=${before}`) : url; // See README for how before and rank cursor works
    url = rank ? url.concat(`&cursor=${rank}`) : url;

    return url;
  };

  const loadMockData = () => {
    import('../dev/feed-mock-data.json').then((mockData) => {
      devLog(
        '[useFetchFeedEntries#loadMockData] >> fetching sample feed entries'
      );
      setEntries((current) => [...current, ...mockData.entries]);
      setIsLoading(false);
      setPagination({
        maxEntries: true,
        hasMore: true // change this to false to stop infinite loading (only the first page will be loaded)
      });
    });
  };

  async function loadEntries({ cursorData }) {
    setIsLoading(true);
    if (useMockData && isDev()) {
      // only load mocked data in development mode to avoid prod data from being manipulated
      // by developers (eg reporting photo, deleting comment)
      await loadMockData();
    } else {
      const url = buildEndpointUrl(cursorData);
      if (url) {
        try {
          devLog('[useFetchFeedEntries#loadMockData] >> fetching feed entries');
          await createNetworkingClient()
            .get(url, { params: {} })
            .then((response) => {
              if (response && response.status === 200) {
                const { data } = response;
                const fetchedEntries = data.entries;
                setEntries((current) => [...current, ...fetchedEntries]);
                setPagination({
                  maxEntries: data.pagination.maxEntries,
                  hasMore:
                    data.pagination.hasMore && fetchedEntries.length !== 0 // show limit message when no entries were fetched
                });
                setIsLoading(false);
              } else {
                // responses other than 200
                errorState(response.error);
              }
            })
            .catch((err) => {
              errorState(err);
            });
        } catch (err) {
          errorState(err);
        }
      } else {
        errorState(new Error(`endpoint for page - '${page}' is not defined`));
      }
    }
  }

  return { isLoading, entries, pagination, error, loadEntries };
};

export default useFetchFeedEntries;
